/**
 * TaxSetu AI - Feedback Modal Component
 * Beta Founders Club - Early Adopter Rewards
 */

import { useState } from 'react';
import { X, Star, Send, MessageSquare, Bug, Lightbulb, Loader2, Trophy, Gift, CheckCircle, Crown, FileText, Scale, Receipt } from 'lucide-react';
import { Button } from "./ui/button";
import { API_BASE_URL } from "../utils/api";

const API_URL = API_BASE_URL;

export default function FeedbackModal({ isOpen, onClose, currentPage }) {
  const [step, setStep] = useState(1); // 1: Form, 2: Success
  const [feedbackType, setFeedbackType] = useState('general');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  
  // Feature vote - category selection
  const [featureCategory, setFeatureCategory] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');
  const [usageFrequency, setUsageFrequency] = useState('');

  // Check if user qualifies for Beta Founders Club (requires email)
  const qualifiesForRewards = () => {
    if (isAnonymous) return false; // Anonymous users don't get rewards
    const hasEmail = email.trim() && email.includes('@');
    const hasName = name.trim().length > 0;
    const answeredQuestions = [
      message.trim().length > 10,
      featureCategory !== '',
      usageFrequency !== ''
    ].filter(Boolean).length;
    
    return hasEmail && hasName && answeredQuestions >= 2;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: isAnonymous ? '' : email,
          name: isAnonymous ? 'Anonymous' : name,
          is_anonymous: isAnonymous,
          feedback_type: feedbackType,
          message,
          rating,
          page: currentPage || window.location.pathname,
          is_beta_founder: qualifiesForRewards(),
          feature_vote: featureCategory,
          feature_description: featureDescription,
          usage_frequency: usageFrequency
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.coupon_code) {
          setCouponCode(data.coupon_code);
        }
        setStep(2);
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setStep(1);
    setMessage('');
    setRating(0);
    setEmail('');
    setName('');
    setIsAnonymous(false);
    setFeatureCategory('');
    setFeatureDescription('');
    setUsageFrequency('');
    setCouponCode('');
    setError('');
  };

  if (!isOpen) return null;

  const featureOptions = [
    { id: 'income_tax', label: 'Income Tax', icon: FileText, color: 'cyan' },
    { id: 'gst', label: 'GST', icon: Receipt, color: 'amber' },
    { id: 'companies_act', label: 'Companies Act', icon: Scale, color: 'purple' }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-slate-800 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-700"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 via-orange-500 to-pink-500 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-white" />
              <h3 className="text-base font-bold text-white">Join the TaxSetu AI Beta Founders Club! 🏆</h3>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Step 2: Success */}
        {step === 2 ? (
          <div className="p-5 text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">Welcome to the Club! 🎉</h4>
            <p className="text-slate-400 mb-3 text-sm">You're now a TaxSetu Beta Founder!</p>
            
            {couponCode && (
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-3 mb-3">
                <p className="text-amber-400 text-xs mb-1">Your Beta Insider Coupon:</p>
                <div className="bg-slate-900 rounded-lg p-2 font-mono text-lg text-white tracking-wider">
                  {couponCode}
                </div>
              </div>
            )}
            
            {/* Rewards Unlocked */}
            <div className="space-y-2 text-left mb-4">
              <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-xs">✅ 1-Month Premium Pass unlocked!</p>
              </div>
              <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2">
                <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <p className="text-cyan-400 text-xs">✅ FREE ITR-1 Filing (July 2026)</p>
              </div>
            </div>
            
            <p className="text-xs text-slate-400 mb-3">
              📧 Check <span className="text-cyan-400">{email}</span> for details!
            </p>
            
            <Button onClick={() => { resetForm(); onClose(); }} className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-sm py-2">
              Awesome, Thanks!
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            
            {/* Rewards Preview - Compact */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-amber-400 font-semibold text-xs mb-2 flex items-center gap-1">
                <Gift className="w-3 h-3" />
                Early Adopter Rewards <span className="text-slate-500 font-normal">(requires email)</span>:
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-300">
                <span>🎁 1-Month Premium</span>
                <span>🗳️ Vote for features</span>
                <span>📄 FREE ITR-1 Filing (July 2026)</span>
              </div>
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsAnonymous(!isAnonymous)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  isAnonymous ? 'bg-cyan-600' : 'bg-slate-600'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  isAnonymous ? 'translate-x-5' : ''
                }`} />
              </button>
              <span className="text-xs text-slate-400">Submit Anonymously</span>
              {isAnonymous && <span className="text-[10px] text-slate-500">(No rewards)</span>}
            </div>

            {/* Name & Email Row - Only show if not anonymous */}
            {!isAnonymous && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1">Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Rahul Sharma"
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    required={!isAnonymous}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    required={!isAnonymous}
                  />
                </div>
              </div>
            )}

            {/* Feedback Type & Rating - Inline */}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1">Type</label>
                <div className="flex gap-1">
                  {[
                    { id: 'general', label: 'General', icon: MessageSquare },
                    { id: 'bug', label: 'Bug', icon: Bug },
                    { id: 'feature', label: 'Feature', icon: Lightbulb }
                  ].map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFeedbackType(type.id)}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs transition-colors ${
                        feedbackType === type.id 
                          ? 'bg-cyan-600 text-white' 
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                      }`}
                    >
                      <type.icon className="w-3 h-3" />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1 text-center">Rate</label>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-0.5"
                    >
                      <Star 
                        className={`w-5 h-5 ${
                          star <= (hoverRating || rating) 
                            ? 'fill-amber-400 text-amber-400' 
                            : 'text-slate-400 dark:text-slate-600'
                        }`} 
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Feedback Text */}
            <div>
              <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1">
                Your Feedback *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's working? What could be better?"
                className="w-full px-2.5 py-2 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-none"
                rows={2}
                required
              />
            </div>

            {/* Feature Vote - 3 Categories */}
            <div>
              <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1.5">🗳️ What should we build next?</label>
              <div className="grid grid-cols-3 gap-2">
                {featureOptions.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setFeatureCategory(opt.id)}
                    className={`p-2 rounded-lg border text-center transition-all ${
                      featureCategory === opt.id 
                        ? opt.color === 'cyan' ? 'bg-cyan-600/20 border-cyan-500 text-cyan-600 dark:text-cyan-400' :
                          opt.color === 'amber' ? 'bg-amber-600/20 border-amber-500 text-amber-600 dark:text-amber-400' :
                          'bg-purple-600/20 border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'bg-slate-100 dark:bg-slate-700/50 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
                    }`}
                  >
                    <opt.icon className={`w-5 h-5 mx-auto mb-1 ${
                      featureCategory === opt.id ?
                        opt.color === 'cyan' ? 'text-cyan-600 dark:text-cyan-400' :
                        opt.color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                        'text-purple-600 dark:text-purple-400'
                      : 'text-slate-500 dark:text-slate-400'
                    }`} />
                    <p className="font-medium text-xs">{opt.label}</p>
                  </button>
                ))}
              </div>
              
              {/* Feature Description - Shows when category selected */}
              {featureCategory && (
                <input
                  type="text"
                  value={featureDescription}
                  onChange={(e) => setFeatureDescription(e.target.value)}
                  placeholder="Describe the feature you'd like to see..."
                  className="w-full mt-2 px-2.5 py-1.5 bg-white dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                />
              )}
            </div>

            {/* Usage Frequency - Compact Pills */}
            <div>
              <label className="block text-[11px] text-slate-600 dark:text-slate-400 mb-1.5">📊 How often will you use TaxSetu?</label>
              <div className="flex gap-1.5 flex-wrap">
                {['Daily', 'Weekly', 'Monthly', 'Occasionally'].map(freq => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setUsageFrequency(freq)}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${
                      usageFrequency === freq 
                        ? 'bg-cyan-600 text-white' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                    }`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
            </div>

            {/* Qualification Status - Only show if not anonymous */}
            {!isAnonymous && (
              <div className={`p-2 rounded-lg border text-center ${
                qualifiesForRewards() 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-slate-700/30 border-slate-600'
              }`}>
                <p className={`text-xs ${qualifiesForRewards() ? 'text-green-400' : 'text-slate-400'}`}>
                  {qualifiesForRewards() 
                    ? '🎉 You qualify for Beta Founders rewards!' 
                    : '⚠️ Answer 2+ questions with email to unlock rewards'}
                </p>
              </div>
            )}

            {error && (
              <p className="text-red-400 text-xs text-center">{error}</p>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || (!isAnonymous && (!name || !email)) || !message}
              className={`w-full ${
                qualifiesForRewards()
                  ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500'
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {qualifiesForRewards() ? 'Submit & Claim Rewards!' : 'Submit Feedback'}
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
